import { renderAngularDimensionArc } from '../services/angular_dimension_render.mjs';
import { ghostSvgAttributes } from '../../plugin-sdk/ghost_preview.mjs';
import { S } from '../app/app_state.js?v=20260817c';
import { svgEl, worldSvgPolygon, worldToScreen } from '../render/plan_renderer.js?v=20260904f';
import {
  PLAN_LABEL_SCALE_MODES,
  resolvePlanLabelMetrics
} from '../render/plan_label_options.js?v=20260828b';
import { isPrintPlanCaptureInProgress } from '../print/print_plan_context.js?v=20260826b';
import {
  SKETCH_WALL_SYSTEM_ID,
  ensureSketchWallState,
  allSketchWalls,
  allSketchAngles,
  sketchNodeById,
  sketchWallById,
  sketchWallOtherNodeId,
  sketchWallCorners,
  sketchWallSideLength,
  sketchAngleDisplayDegrees,
  sketchPointAtOffsetOnWall,
  sketchWallOpeningsWithLabels,
  sketchWallOpeningRegions,
  sketchWallFurring,
  sketchWallFurringQuad,
  sketchWallIsSelected
} from './sketch_wall_state.js?v=20260904f';
import {
  bindSketchWallInteraction,
  bindSketchWallOpeningInteraction,
  bindSketchLengthLabelInteraction,
  bindSketchAngleLabelInteraction,
  ensureSketchWallGlobalListeners,
  getSketchWallTemplatePreview
} from './sketch_wall_interaction.js?v=20260903c';
import { findColor, resolveColorHex } from '../../../colors/color_catalog_registry.js?v=20260720a';

// These values preserve the previous appearance at the default 0.25 px/mm
// camera scale, but are model dimensions now. Cached navigation transforms
// and fresh renders therefore agree, and the visuals become smaller when the
// user zooms out instead of snapping back to fixed CSS-pixel sizes.
const WALL_FRAME_WIDTH_MM = 3;
const WALL_SELECTED_FRAME_WIDTH_MM = 5;
const WALL_FURRING_DASH_MM = 12;
const WALL_FURRING_GAP_MM = 8;

const LENGTH_LABEL_REFERENCE_FONT_PX = 12;
const ANGLE_LABEL_REFERENCE_FONT_PX = 11;
const LABEL_HALO_REFERENCE_WIDTH_PX = 3;
const LENGTH_LABEL_FACE_OFFSET_MM = 40;
const ANGLE_ARC_REFERENCE_RADIUS_PX = 26;
const ANGLE_LABEL_REFERENCE_OFFSET_PX = 40;
const ANGLE_ARC_REFERENCE_STROKE_PX = 1.35;

function cameraScale() {
  const scale = Number(S.camera2D?.scale);
  return Number.isFinite(scale) && scale > 0 ? scale : 1;
}

function screenLength(worldLengthMm) {
  return worldLengthMm * cameraScale();
}

function sketchWallPlanLabelMetrics() {
  const printPlan = isPrintPlanCaptureInProgress();
  const scaleMode = printPlan ? PLAN_LABEL_SCALE_MODES.SCREEN : PLAN_LABEL_SCALE_MODES.MODEL;
  const labelMetrics = screenFontSizePx => ({
    ...resolvePlanLabelMetrics({
      cameraScale: cameraScale(),
      scaleMode,
      screenFontSizePx
    }),
    printPlan
  });
  return {
    length: labelMetrics(LENGTH_LABEL_REFERENCE_FONT_PX),
    angle: labelMetrics(ANGLE_LABEL_REFERENCE_FONT_PX)
  };
}

function readableWallLabelAngleDeg(start, end) {
  let angle = Math.atan2(end.y - start.y, end.x - start.x) * 180 / Math.PI;
  while (angle > 90) angle -= 180;
  while (angle <= -90) angle += 180;
  return Math.abs(angle) < 0.0001 ? 0 : angle;
}

function sketchWallPlanLabelVisual(parent, metrics, referenceFontPx) {
  const attrs = { class: 'plan-label-visual sketch-wall-plan-label-visual' };
  if (metrics.printPlan) {
    attrs['data-plan-label-output-scale'] = 'true';
    attrs['data-plan-label-reference-px'] = referenceFontPx;
    attrs['data-plan-label-rendered-px'] = metrics.fontSizePx;
  }
  return svgEl('g', attrs, parent);
}

function drawSketchWallLengthLabel(parent, wall, side, position, angle, length, metrics) {
  const root = svgEl('g', {
    class: `sketch-wall-length-label-root sketch-wall-length-${side}-root`,
    transform: `translate(${position.x} ${position.y}) rotate(${angle})`,
    'data-plan-label-role': 'dimension',
    'data-plan-label-size': metrics.size,
    'data-plan-label-scale-mode': metrics.scaleMode,
    'data-plan-label-font-size-mm': metrics.fontSizeMm
  }, parent);
  const visual = sketchWallPlanLabelVisual(root, metrics, LENGTH_LABEL_REFERENCE_FONT_PX);
  const label = svgEl('text', {
    x: 0,
    y: 0,
    class: `sketch-wall-length-text sketch-wall-length-${side}${sketchWallLengthStateClass(wall, side)}`,
    'text-anchor': 'middle',
    'dominant-baseline': 'middle',
    'data-plan-label-size': metrics.size,
    'data-plan-label-scale-mode': metrics.scaleMode,
    'data-plan-label-font-size-mm': metrics.fontSizeMm,
    'font-size': metrics.fontSizePx,
    'stroke-width': LABEL_HALO_REFERENCE_WIDTH_PX
      * (metrics.fontSizePx / LENGTH_LABEL_REFERENCE_FONT_PX)
  }, visual);
  label.textContent = `${Math.round(length)} mm`;
  bindSketchLengthLabelInteraction(label, wall, side);
}

function sketchRenderPoint(nodeId) {
  const node = sketchNodeById(nodeId);
  return node ? { x: node.x, z: node.z } : { x: 0, z: 0 };
}

// Red while underdetermined, same as every other dimension. Once
// determined, the face the user actually set (wall.lengthMode) stays the
// default black — the "master" — while the other face, which only follows
// along as a consequence (thickness/angles), is shown brown, so it's always
// visible at a glance which dimension is driving the wall vs. which is
// derived — especially once thickness or an adjoining angle changes.
function sketchWallLengthStateClass(wall, side) {
  const free = side === 'inner' ? wall.innerFree : wall.outerFree;
  if (free !== false) return ' is-free';
  return wall.lengthMode === side ? '' : ' is-derived';
}

// The 2D plan view stays flat (no photo texture — that's reserved for 3D),
// but still tints the wall body so different Wandart/Wandfarbe choices are
// visually distinguishable at a glance. Mirrors the 3D tint precedence: an
// explicit wallColor wins, otherwise a paintable texture's defaultColor,
// otherwise the texture's own representative swatch hex. `wallColorOverride`
// lets the Vormauerung reuse this with its own color instead of the wall's
// (still against the wall's own Wandart texture, which it always shares).
function sketchWallFillColorHex(wall, wallColorOverride) {
  const entry = findColor('texturen_wand', wall.hatchType);
  const colorValue = wallColorOverride !== undefined ? wallColorOverride : wall.wallColor;
  const tint = colorValue ? resolveColorHex(colorValue, null) : null;
  return tint || entry?.defaultColor || entry?.hex || null;
}

function drawSketchWall(parent, wall, labelMetrics, gpuBody = false) {
  const startNode = sketchNodeById(wall.startNodeId);
  const endNode = sketchNodeById(wall.endNodeId);
  if (!startNode || !endNode) return;
  const getPoint = sketchRenderPoint;
  const corners = sketchWallCorners(wall, getPoint);
  const selected = sketchWallIsSelected(wall.id);
  const fillColor = sketchWallFillColorHex(wall);
  const polygon = worldSvgPolygon(parent, [corners.startInner, corners.endInner, corners.endOuter, corners.startOuter], {
    class: `sketch-wall-body${wall.free === false ? '' : ' is-free'}${wall.fixed ? ' is-wall-fixed' : ''}${selected ? ' is-selected' : ''}`,
    'data-sketch-wall-id': wall.id,
    'data-dimension-owner-id': SKETCH_WALL_SYSTEM_ID,
    'data-dimension-source-id': wall.id,
    'data-dimension-source-label': 'Parametrische Wand',
    'stroke-width': screenLength(selected ? WALL_SELECTED_FRAME_WIDTH_MM : WALL_FRAME_WIDTH_MM),
    ...(fillColor ? { style: `fill:${fillColor};fill-opacity:0.38` } : {})
  });
  if (gpuBody) {
    polygon.style.fill = 'transparent';
    polygon.style.stroke = selected ? '#2563eb' : 'transparent';
    polygon.style.pointerEvents = 'all';
  }
  bindSketchWallInteraction(polygon, wall);

  const dx = endNode.x - startNode.x;
  const dz = endNode.z - startNode.z;
  const len = Math.hypot(dx, dz) || 1;
  const nx = -dz / len;
  const nz = dx / len;

  const innerMid = { x: (corners.startInner.x + corners.endInner.x) / 2, z: (corners.startInner.z + corners.endInner.z) / 2 };
  const outerMid = { x: (corners.startOuter.x + corners.endOuter.x) / 2, z: (corners.startOuter.z + corners.endOuter.z) / 2 };
  // At the medium global preset this is 40 mm from the relevant wall face.
  // Scaling the offset with the label itself keeps small/large balanced and
  // lets the print capture's fixed-screen override retain a readable gap.
  const faceOffsetPx = labelMetrics.fontSizePx
    * (LENGTH_LABEL_FACE_OFFSET_MM / 45);

  const innerLength = wall.lengthMode === 'inner' && Number.isFinite(wall.length) ? wall.length : sketchWallSideLength(wall, getPoint, 1);
  const outerLength = wall.lengthMode === 'outer' && Number.isFinite(wall.length) ? wall.length : sketchWallSideLength(wall, getPoint, -1);

  const wallStartScreen = worldToScreen(startNode.x, startNode.z);
  const wallEndScreen = worldToScreen(endNode.x, endNode.z);
  const labelAngle = readableWallLabelAngleDeg(wallStartScreen, wallEndScreen);

  const innerMidScreen = worldToScreen(innerMid.x, innerMid.z);
  const innerScreen = {
    x: innerMidScreen.x + nx * faceOffsetPx,
    y: innerMidScreen.y + nz * faceOffsetPx
  };
  drawSketchWallLengthLabel(
    parent,
    wall,
    'inner',
    innerScreen,
    labelAngle,
    innerLength,
    labelMetrics
  );

  const outerMidScreen = worldToScreen(outerMid.x, outerMid.z);
  const outerScreen = {
    x: outerMidScreen.x - nx * faceOffsetPx,
    y: outerMidScreen.y - nz * faceOffsetPx
  };
  drawSketchWallLengthLabel(
    parent,
    wall,
    'outer',
    outerScreen,
    labelAngle,
    outerLength,
    labelMetrics
  );
}

// The Vormauerung reads in plan as "this wall is thicker along here" —
// drawn with the same fill/selection styling as the wall body itself (see
// drawSketchWall) and bound to the same click/drag interaction (dragging it
// still moves the whole underlying wall — the Vormauerung has no position
// of its own). It IS its own distinct click target though (`furring: true`),
// so a plain click here is told apart from clicking the main wall body —
// that's what lets the sidebar bring the Vormauerung card forward instead
// of Parametrische Wände (see sketchWallSidebarCardForSelection).
function drawSketchWallFurring(parent, wall) {
  const furring = sketchWallFurring(wall);
  if (!furring.enabled) return;
  const getPoint = sketchRenderPoint;
  const corners = sketchWallCorners(wall, getPoint);
  const polygon = sketchWallFurringQuad(wall, getPoint, corners);
  const selected = sketchWallIsSelected(wall.id);
  const fillColor = sketchWallFillColorHex(wall, furring.wallColor);
  const furringPolygon = worldSvgPolygon(parent, polygon, {
    class: `sketch-wall-furring-body${wall.free === false ? '' : ' is-free'}${wall.fixed ? ' is-wall-fixed' : ''}${selected ? ' is-selected' : ''}`,
    'data-sketch-wall-id': wall.id,
    'stroke-width': screenLength(selected ? WALL_SELECTED_FRAME_WIDTH_MM : WALL_FRAME_WIDTH_MM),
    'stroke-dasharray': `${screenLength(WALL_FURRING_DASH_MM)} ${screenLength(WALL_FURRING_GAP_MM)}`,
    ...(fillColor ? { style: `fill:${fillColor};fill-opacity:0.38` } : {})
  });
  bindSketchWallInteraction(furringPolygon, wall, { furring: true });
}

const OPENING_HANDLE_RADIUS_PX = 4;
const OPENING_LABEL_OFFSET_PX = 20;
const OPENING_LABEL_LINE_HEIGHT_PX = 11;

// Ausschnitte are deliberately NOT typed as door/window in the drawing —
// every one gets the same dashed "gap" look regardless of its sill height,
// plus its own small draggable handle and a 2-line "Ausschnitt N" / size
// label. The white background is drawn per MERGED region
// (sketchWallOpeningRegions) so two overlapping openings share one
// continuous blank area, but each opening ALSO gets its own outline drawn
// on top — always, not just when selected — since otherwise two
// overlapping openings share the merged region's single outer border and
// their actual individual boundaries become invisible (they still stay
// independently selectable/draggable while visually merged). The selected
// one's outline is simply drawn last, in the distinct "is-selected" style,
// so it's never hidden under an unselected neighbor's line.
function drawSketchWallOpenings(parent, wall) {
  const getPoint = sketchRenderPoint;
  for (const region of sketchWallOpeningRegions(wall)) {
    const corners = [
      sketchPointAtOffsetOnWall(wall, region.startOffset, 1, getPoint),
      sketchPointAtOffsetOnWall(wall, region.endOffset, 1, getPoint),
      sketchPointAtOffsetOnWall(wall, region.endOffset, -1, getPoint),
      sketchPointAtOffsetOnWall(wall, region.startOffset, -1, getPoint)
    ];
    worldSvgPolygon(parent, corners, { class: 'sketch-wall-opening-gap' });
  }

  const sel = S.selection?.sketchWall;
  const selectedOpeningId = sel?.wallId === wall.id ? sel?.openingId : null;
  const openings = sketchWallOpeningsWithLabels(wall);
  const orderedOpenings = [...openings].sort((a, b) => (a.id === selectedOpeningId ? 1 : 0) - (b.id === selectedOpeningId ? 1 : 0));

  for (const opening of orderedOpenings) {
    const selected = opening.id === selectedOpeningId;
    const halfWidth = opening.width / 2;
    const outline = [
      sketchPointAtOffsetOnWall(wall, opening.offset - halfWidth, 1, getPoint),
      sketchPointAtOffsetOnWall(wall, opening.offset + halfWidth, 1, getPoint),
      sketchPointAtOffsetOnWall(wall, opening.offset + halfWidth, -1, getPoint),
      sketchPointAtOffsetOnWall(wall, opening.offset - halfWidth, -1, getPoint)
    ];
    worldSvgPolygon(parent, outline, { class: `sketch-wall-opening-outline${selected ? ' is-selected' : ''}` });

    const center = sketchPointAtOffsetOnWall(wall, opening.offset, 0, getPoint);
    const screen = worldToScreen(center.x, center.z);

    // Handle + both label lines live in one group with a single interaction
    // binding, so clicking/dragging the (small) circle OR the text — either
    // line — selects/moves the same opening; the text no longer needs its
    // own pointer-events:none since the group is the real hit target.
    const group = svgEl('g', { class: `sketch-wall-opening-group${selected ? ' is-selected' : ''}` }, parent);
    bindSketchWallOpeningInteraction(group, wall, opening);

    svgEl('circle', {
      cx: screen.x,
      cy: screen.y,
      r: OPENING_HANDLE_RADIUS_PX,
      class: `sketch-wall-opening-handle${selected ? ' is-selected' : ''}`
    }, group);

    const label = svgEl('text', {
      x: screen.x,
      y: screen.y - OPENING_LABEL_OFFSET_PX - OPENING_LABEL_LINE_HEIGHT_PX,
      class: `sketch-wall-opening-label${selected ? ' is-selected' : ''}`,
      'text-anchor': 'middle'
    }, group);
    svgEl('tspan', { x: screen.x, dy: 0 }, label).textContent = opening.label;
    svgEl('tspan', { x: screen.x, dy: OPENING_LABEL_LINE_HEIGHT_PX }, label).textContent = `${Math.round(opening.width)} × ${Math.round(opening.height)} mm`;
  }
}

function drawSketchAngle(parent, angle, labelMetrics) {
  const node = sketchNodeById(angle.nodeId);
  const wallA = sketchWallById(angle.wallAId);
  const wallB = sketchWallById(angle.wallBId);
  if (!node || !wallA || !wallB) return;
  const otherA = sketchNodeById(sketchWallOtherNodeId(wallA, angle.nodeId));
  const otherB = sketchNodeById(sketchWallOtherNodeId(wallB, angle.nodeId));
  if (!otherA || !otherB) return;

  const center = worldToScreen(node.x, node.z);
  const screenA = worldToScreen(otherA.x, otherA.z);
  const screenB = worldToScreen(otherB.x, otherB.z);
  const angleA = Math.atan2(screenA.y - center.y, screenA.x - center.x);
  const angleB = Math.atan2(screenB.y - center.y, screenB.x - center.x);
  let delta = angleB - angleA;
  while (delta < -Math.PI) delta += Math.PI * 2;
  while (delta > Math.PI) delta -= Math.PI * 2;
  const scaleFactor = labelMetrics.fontSizePx / ANGLE_LABEL_REFERENCE_FONT_PX;
  const arcRadius = ANGLE_ARC_REFERENCE_RADIUS_PX * scaleFactor;
  const labelOffset = ANGLE_LABEL_REFERENCE_OFFSET_PX * scaleFactor;
  const haloWidth = LABEL_HALO_REFERENCE_WIDTH_PX * scaleFactor;
  const root = svgEl('g', {
    class: 'sketch-wall-angle-label-root',
    transform: `translate(${center.x} ${center.y})`,
    'data-plan-label-role': 'angle',
    'data-plan-label-size': labelMetrics.size,
    'data-plan-label-scale-mode': labelMetrics.scaleMode,
    'data-plan-label-font-size-mm': labelMetrics.fontSizeMm
  }, parent);
  const visual = sketchWallPlanLabelVisual(
    root,
    labelMetrics,
    ANGLE_LABEL_REFERENCE_FONT_PX
  );
  const steps = Math.max(2, Math.ceil(Math.abs(delta) / (Math.PI / 180)));
  const points = Array.from({ length: steps + 1 }, (_, i) => ({
    x: arcRadius * Math.cos(angleA + delta * i / steps),
    y: arcRadius * Math.sin(angleA + delta * i / steps)
  }));
  const visualScale = labelMetrics.fontSizePx / 12;
  renderAngularDimensionArc(visual, points, {
    svgEl, visualScale,
    lineAttrs: { class: `sketch-wall-angle-arc${angle.free === false ? '' : ' is-free'}`,
      'stroke-width': ANGLE_ARC_REFERENCE_STROKE_PX * visualScale, 'pointer-events': 'none' },
    arrowAttrs: { class: 'sketch-wall-angle-arrow', fill: angle.free === false ? '#111827' : '#dc2626', 'pointer-events': 'none' }
  });

  const bisector = angleA + delta / 2;
  const labelPoint = {
    x: labelOffset * Math.cos(bisector),
    y: labelOffset * Math.sin(bisector)
  };
  // Always display the angle the geometry actually reached. A stored target
  // must never make the label claim 9° while the wall still sits near 10°.
  const degrees = sketchAngleDisplayDegrees(angle);
  const label = svgEl('text', {
    x: labelPoint.x,
    y: labelPoint.y,
    class: `sketch-wall-angle-text${angle.free === false ? '' : ' is-free'}`,
    'text-anchor': 'middle',
    'dominant-baseline': 'middle',
    'data-plan-label-size': labelMetrics.size,
    'data-plan-label-scale-mode': labelMetrics.scaleMode,
    'data-plan-label-font-size-mm': labelMetrics.fontSizeMm,
    'font-size': labelMetrics.fontSizePx,
    'stroke-width': haloWidth
  }, visual);
  label.textContent = `${String(degrees).replace('.', ',')}°`;
  bindSketchAngleLabelInteraction(label, angle);
}

function drawSketchWallDraftPreview(parent) {
  const template = getSketchWallTemplatePreview();
  if (template && !isPrintPlanCaptureInProgress()) {
    const ghost = svgEl('g', { 'data-sketch-wall-template-ghost': '', 'pointer-events': 'none', ...ghostSvgAttributes() }, parent);
    template.polygons.forEach(corners => {
      const points = [corners.startInner, corners.endInner, corners.endOuter, corners.startOuter].map(p => worldToScreen(p.x, p.z));
      svgEl('polygon', { points: points.map(p => `${p.x},${p.y}`).join(' '), fill: '#94a3b8', 'fill-opacity': 0.22 }, ghost);
    });
    return;
  }
  const draw = S.ui?.sketchWallDraw;
  if (!draw?.startNodeId || !draw.currentPoint) return;
  const startNode = sketchNodeById(draw.startNodeId);
  if (!startNode) return;
  const start = worldToScreen(startNode.x, startNode.z);
  const end = worldToScreen(draw.currentPoint.x, draw.currentPoint.z);
  svgEl('line', {
    x1: start.x,
    y1: start.y,
    x2: end.x,
    y2: end.y,
    class: 'sketch-wall-draft-preview', ...ghostSvgAttributes(), fill: 'none'
  }, parent);
}

function drawSketchWalls2D(parent, { gpuWallIds } = {}) {
  if (!parent) return;
  ensureSketchWallState();
  ensureSketchWallGlobalListeners();
  while (parent.firstChild) parent.removeChild(parent.firstChild);
  const labelMetrics = sketchWallPlanLabelMetrics();
  for (const wall of allSketchWalls()) {
    drawSketchWall(parent, wall, labelMetrics.length, gpuWallIds?.has(wall.id));
    drawSketchWallFurring(parent, wall);
    drawSketchWallOpenings(parent, wall);
  }
  for (const angle of allSketchAngles()) drawSketchAngle(parent, angle, labelMetrics.angle);
  drawSketchWallDraftPreview(parent);
}

export { drawSketchWalls2D, sketchWallFillColorHex };
